import React, { useEffect, useId, useMemo, useState } from "react";
import {
  Box,
  InputAdornment,
  Typography,
  Divider,
  TableCell,
  Tooltip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CommonCalculator from "../../../builders/calculator/common";
import PdfGenerator from "./Pdf/FurtherDeductionPdf";
import {
  formatAmount,
  formatHistoryAmount,
  isAutoload,
} from "../../../utils/hooks/dataHandler";
import YesNoSwitch from "../../utils/button/YesNoSwitch";
import TaxField from "../../utils/TextField/TaxField";
import ScheduleHeader from "../../App/ScheduleHeader";
import ScheduleNotes from "../ScheduleNotes";
import CommonTable from "../../utils/CommonTable";
import {
  TextFieldForName,
  TextFieldForViewOnly,
} from "../../utils/TextField/TextField";
import { CalculatorTextField } from "../../utils/TextField/NumericTextField";
import IconButtonWithTooltip from "../../utils/IconButtonWithTooltip";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Paper from "@mui/material/Paper";
import SectionBox from "../utils/SectionBox";
import ScheduleContainer from "../utils/ScheduleContainer";

const settingsRowSx = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 2,
};

const settingsGridSx = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 240px), 1fr))",
  gap: 3,
  mt: 3,
};

const switchSx = {
  flexShrink: 0,
  "& .MuiToggleButton-root": {
    minWidth: 64,
    minHeight: 44,
    fontSize: "1rem",
    fontWeight: 600,
    textTransform: "none",
  },
};

const DeductionField = ({ label, children }) => {
  const id = useId();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
      <Typography component='label' htmlFor={id} fontWeight={600}>
        {label}
      </Typography>
      {children({ id, "aria-label": label })}
    </Box>
  );
};

const deductionNumberProps = {
  fullWidth: true,
  size: "small",
  allowNegative: false,
  preserveEmpty: true,
  // Retain fractional rates and amounts without padding the display with zeros.
  decimalScale: 20,
  trimTrailingZeros: true,
  FormHelperTextProps: { sx: { mx: 0, mt: 1, lineHeight: 1.5 } },
};

const FurtherDeductionSchedule = ({
  company,
  schedule,
  scheduleDetails,
  saveSchedule,
  deleteSchedule,
  canEditSchedule = true,
}) => {
  const currency = company.currency || "SGD";
  const isForeignCurrency = currency !== "SGD";
  const defaultExchangeRate = isForeignCurrency
    ? company.manual_currency_rate || company.currencyRate || 1
    : 1;
  const [schName, setSchName] = useState("Further Deductions under Section");
  const [notes, setNotes] = useState("");
  const [reviewNotes, setReviewNotes] = useState("");

  const [expensesRows, setExpensesRows] = useState([]);
  const [totalExpenses, setTotalExpenses] = useState(0);
  const [isExpenseCapped, setIsExpenseCapped] = useState(true);
  const [deductionRate, setDeductionRate] = useState(50);
  const [expenseCappedAmount, setExpenseCappedAmount] = useState("0");
  const [finalDeduction, setFinalDeduction] = useState(0);
  const [exchangeRate, setExchangeRate] = useState(defaultExchangeRate);
  const [isExcessDeductionEnabled, setIsExcessDeductionEnabled] =
    useState(false);
  const [excessDeductionRate, setExcessDeductionRate] = useState(0);
  const [excessDeductionDescription, setExcessDeductionDescription] =
    useState("");

  const {
    isExchangeRateValid,
    calculationExchangeRate,
    excessDeduction,
    totalDeduction,
  } = useMemo(() => {
    const numericExchangeRate = Number(exchangeRate);
    const numericExcessDeductionRate = Number(excessDeductionRate);
    const isExchangeRateValid =
      Number.isFinite(numericExchangeRate) && numericExchangeRate > 0;
    const calculationExchangeRate = isExchangeRateValid
      ? numericExchangeRate
      : 1;
    const excessExpenses =
      isExpenseCapped && isExcessDeductionEnabled && expenseCappedAmount
        ? Math.max(
            totalExpenses - expenseCappedAmount / calculationExchangeRate,
            0
          )
        : 0;
    const excessDeduction = excessExpenses * (numericExcessDeductionRate / 100);

    return {
      isExchangeRateValid,
      calculationExchangeRate,
      excessDeduction,
      totalDeduction: finalDeduction + excessDeduction,
    };
  }, [
    exchangeRate,
    excessDeductionRate,
    isExpenseCapped,
    isExcessDeductionEnabled,
    expenseCappedAmount,
    totalExpenses,
    finalDeduction,
  ]);

  const handleSave = () => {
    if (isExpenseCapped && !isExchangeRateValid) {
      return;
    }

    schedule.name = schName;
    schedule.notes = notes;
    schedule.reviewNotes = reviewNotes;
    saveSchedule(schedule, {
      expensesRows: expensesRows,
      expenseCappedAmount: expenseCappedAmount,
      deductionRate: deductionRate,
      // Keep the original deduction separate from the excess deduction in main comp.
      totalAdjustment: finalDeduction,
      isExpenseCapped: isExpenseCapped,
      isExcessDeductionEnabled,
      exchangeRate,
      excessDeductionRate,
      excessDeductionDescription,
      excessDeduction,
      totalDeduction,
      doc: JSON.stringify({
        expensesRows,
        totalExpenses,
        isExpenseCapped,
        isExcessDeductionEnabled,
        deductionRate,
        expenseCappedAmount,
        finalDeduction,
        exchangeRate,
        excessDeductionRate,
        excessDeductionDescription,
        excessDeduction,
        totalDeduction,
        notes,
        reviewNotes,
      }),
    });
  };

  const calculateDeductionAmount = (
    value,
    cappedAmount,
    manual_td,
    rate = calculationExchangeRate
  ) => {
    let total = manual_td ?? totalExpenses;

    // Convert the SGD cap to functional currency; retain the existing cap check.
    if (cappedAmount && cappedAmount / rate < total)
      total = Math.min(cappedAmount / rate, total);

    let final = total * (value / 100);
    return final;
  };

  const handleDeductionRate = (value) => {
    if (value === "" || value >= 0) {
      setDeductionRate(value);

      let final = calculateDeductionAmount(
        value,
        isExpenseCapped ? expenseCappedAmount : null,
        totalExpenses
      );
      setFinalDeduction(final);
    }
  };

  const handleExpenseCappedAmount = (value) => {
    if (value === "" || value >= 0) {
      setExpenseCappedAmount(value);

      let final = calculateDeductionAmount(
        deductionRate,
        isExpenseCapped ? value : null,
        totalExpenses
      );
      setFinalDeduction(final);
    }
  };

  const handleIsExpenseCapped = (checked) => {
    setIsExpenseCapped(checked);
    if (!checked) setIsExcessDeductionEnabled(false);

    const final = calculateDeductionAmount(
      deductionRate,
      checked ? expenseCappedAmount : null,
      totalExpenses
    );
    setFinalDeduction(final);
  };

  const handleExchangeRate = (value) => {
    if (
      value === "" ||
      (Number.isFinite(Number(value)) && Number(value) >= 0)
    ) {
      setExchangeRate(value);
      setFinalDeduction(
        calculateDeductionAmount(
          deductionRate,
          isExpenseCapped ? expenseCappedAmount : null,
          totalExpenses,
          Number(value) > 0 ? Number(value) : 1
        )
      );
    }
  };

  const calculateTotals = (
    expensesRows,
    deductionRate,
    isExpenseCapped,
    expenseCappedAmount,
    rate = calculationExchangeRate
  ) => {
    let totalE = CommonCalculator.arraySum(expensesRows);
    setTotalExpenses(totalE);
    const final = calculateDeductionAmount(
      deductionRate ?? 50,
      isExpenseCapped ? expenseCappedAmount : null,
      totalE,
      rate
    );
    setFinalDeduction(final);
  };

  const handleInputChange = (value, { index, field }) => {
    const updatedRows = [...expensesRows];
    if (field === "amount") {
      updatedRows[index]["historyAmount"] = value;
      updatedRows[index][field] = value.reduce((sum, num) => sum + num, 0);

      calculateTotals(
        updatedRows,
        deductionRate,
        isExpenseCapped,
        expenseCappedAmount
      );
    } else {
      updatedRows[index][field] = value;
    }

    setExpensesRows(updatedRows);
  };

  useEffect(() => {
    //init
    if (schedule) {
      setSchName(schedule.name);
      setNotes(schedule.notes);
      setReviewNotes(schedule.reviewNotes);
    }

    if (scheduleDetails) {
      setExpensesRows(
        scheduleDetails.expensesRows.map((row) => {
          if (!row.historyAmount || row.historyAmount.length <= 0) {
            row.historyAmount = [row.amount];
          }
          return row;
        })
      );

      setIsExpenseCapped(scheduleDetails.isExpenseCapped);

      if (scheduleDetails.expenseCappedAmount) {
        setExpenseCappedAmount(scheduleDetails.expenseCappedAmount);
      } else {
        setExpenseCappedAmount(null);
      }

      setDeductionRate(scheduleDetails.deductionRate ?? 50);
      const savedExchangeRate = isForeignCurrency
        ? (scheduleDetails.exchangeRate ?? defaultExchangeRate)
        : 1;
      setExchangeRate(savedExchangeRate);
      setIsExcessDeductionEnabled(
        Boolean(scheduleDetails.isExpenseCapped) &&
          (scheduleDetails.isExcessDeductionEnabled ??
            Number(scheduleDetails.excessDeductionRate) > 0)
      );
      setExcessDeductionRate(scheduleDetails.excessDeductionRate ?? 0);
      setExcessDeductionDescription(
        scheduleDetails.excessDeductionDescription ?? ""
      );

      //calculation
      calculateTotals(
        scheduleDetails.expensesRows,
        scheduleDetails.deductionRate,
        scheduleDetails.isExpenseCapped,
        scheduleDetails.expenseCappedAmount,
        Number.isFinite(Number(savedExchangeRate)) &&
          Number(savedExchangeRate) > 0
          ? Number(savedExchangeRate)
          : 1
      );
    }
  }, []);

  const addRow = (setRows) => {
    setRows((prevRows) => [
      ...prevRows,
      {
        description: "",
        amount: "",
        reference: "",
      },
    ]);
  };

  const removeRow = (setRows, index) => {
    const updatedRows = expensesRows.filter((_, i) => i !== index);
    setRows(updatedRows);
    calculateTotals(
      updatedRows,
      deductionRate,
      isExpenseCapped,
      expenseCappedAmount
    );
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3, lg: 4 }, minWidth: 0 }}>
      <ScheduleHeader
        {...{
          title: schedule.type,
          companyName: company.name,
          uen: company.uen,
          yearOfAssessment: company.yearOfAssessment,
          financialYearStart: company.financialYearStart,
          financialYearEnd: company.financialYearEnd,
          handleSave,
          deleteSchedule,
          canEditSchedule,
        }}
      >
        <PdfGenerator
          schedule={schedule}
          company={company}
          expensesRows={expensesRows}
          totalExpenses={totalExpenses}
          isExpenseCapped={isExpenseCapped}
          isExcessDeductionEnabled={isExcessDeductionEnabled}
          deductionRate={deductionRate}
          expenseCappedAmount={expenseCappedAmount}
          finalDeduction={finalDeduction}
          exchangeRate={exchangeRate}
          excessDeductionRate={excessDeductionRate}
          excessDeductionDescription={excessDeductionDescription}
          excessDeduction={excessDeduction}
          totalDeduction={totalDeduction}
          notes={notes}
          reviewNotes={reviewNotes}
        />
      </ScheduleHeader>
      <br />

      <ScheduleContainer
        key={schedule.id}
        id={schedule.id}
        schedule={schedule}
        schName={schName}
        taxAdjustment={"Deduct"}
        allowedToEditSchName
        updateHeading={(newTitle) => {
          schedule.name = newTitle;
          setSchName(newTitle);
        }}
        canEditSchedule={canEditSchedule}
      >
        {(fullScreenMode) => (
          <>
            {/* Expenses */}
            <SectionBox
              title='Qualifying Expenses'
              onAdd={() => addRow(setExpensesRows)}
              canEdit={canEditSchedule}
            >
              <CommonTable
                rows={expensesRows}
                columns={[
                  {
                    key: "name",
                    label: "Description",
                    headerAlign: "left",
                    render: (row, index) => (
                      <TextFieldForName
                        value={row.description}
                        onChange={handleInputChange}
                        reference={{
                          index: index,
                          field: "description",
                        }}
                        wordLimit={60}
                        disabled={isAutoload(row.status) || !canEditSchedule}
                        canEdit={canEditSchedule}
                      />
                    ),
                  },
                  {
                    key: "amount",
                    label: "Amount",
                    headerAlign: "right",
                    colAlign: "right",
                    render: (row, index) => (
                      <Tooltip
                        title={
                          row?.historyAmount &&
                          row.historyAmount.length > 0 &&
                          formatHistoryAmount(row.historyAmount)
                        }
                        placement='top'
                      >
                        <Box display='inline-block' sx={{ width: "100%" }}>
                          <CalculatorTextField
                            value={row?.historyAmount || []}
                            onChange={handleInputChange}
                            reference={{
                              index: index,
                              field: "amount",
                            }}
                            fullWidth={true}
                            disabled={
                              isAutoload(row.status) || !canEditSchedule
                            }
                            canEdit={canEditSchedule}
                          />
                        </Box>
                      </Tooltip>
                    ),
                  },
                  {
                    key: "reference",
                    label: "Reference",
                    headerAlign: "center",
                    render: (row, index) => (
                      <TextFieldForName
                        value={row?.reference}
                        onChange={handleInputChange}
                        reference={{
                          index: index,
                          field: "reference",
                        }}
                        canEdit={canEditSchedule}
                        viewOnlyAlign='center'
                      />
                    ),
                  },
                  {
                    key: "source",
                    label: "Sch",
                    headerAlign: "center",
                    render: (row) => (
                      <TextFieldForViewOnly value={row.source?.sch_no || ""} />
                    ),
                  },
                  canEditSchedule
                    ? {
                        key: "action",
                        label: "Action",
                        headerAlign: "center",
                        render: (row, index) => (
                          <IconButtonWithTooltip
                            icon={DeleteOutlineIcon}
                            tooltipTitle='Delete Row'
                            onClick={() => removeRow(setExpensesRows, index)}
                            color='error'
                            size='small'
                            disabled={
                              isAutoload(row.status) || !canEditSchedule
                            }
                          />
                        ),
                      }
                    : [],
                ]}
              >
                <TableCell
                  align='right'
                  sx={{
                    fontWeight: "bold",
                  }}
                >
                  Total Expenses
                </TableCell>
                <TableCell
                  align='right'
                  sx={{
                    fontWeight: "bold",
                  }}
                >
                  {formatAmount(totalExpenses)}
                </TableCell>
                <TableCell colSpan={canEditSchedule ? 4 : 3} />
              </CommonTable>
            </SectionBox>

            <Paper
              component='section'
              aria-label='Deduction settings'
              elevation={0}
              sx={{
                border: 1,
                borderColor: "divider",
                boxShadow: "0 3px 16px rgba(30, 20, 50, 0.025)",
                minWidth: 0,
                overflowWrap: "anywhere",
                "& label": { fontSize: "1rem", lineHeight: 1.5 },
                "& .MuiOutlinedInput-root": { borderRadius: 2, minHeight: 48 },
                "& .MuiInputBase-input": { fontSize: "1rem", minWidth: 0 },
                "& .MuiFormHelperText-root": { fontSize: "0.875rem" },
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: "text.primary",
                },
              }}
            >
              <Box
                sx={{
                  ...settingsRowSx,
                  p: { xs: 2, sm: 3 },
                  gap: 3,
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.035),
                }}
              >
                <Box sx={{ flex: "1 1 240px", minWidth: 0 }}>
                  <Typography component='h2' variant='h6' fontWeight={600}>
                    Further deduction
                  </Typography>
                  <Typography
                    color='text.secondary'
                    sx={{ mt: 0.5, lineHeight: 1.6 }}
                  >
                    {isExpenseCapped
                      ? "The deduction rate applies to qualifying expenses up to the expense cap."
                      : "The deduction rate applies to all qualifying expenses."}
                  </Typography>
                </Box>
                <Box sx={{ width: 180, maxWidth: "100%", flexShrink: 0 }}>
                  <DeductionField label='Deduction rate'>
                    {(inputProps) => (
                      <TaxField
                        {...deductionNumberProps}
                        value={deductionRate}
                        onChange={handleDeductionRate}
                        inputProps={{ ...inputProps, inputMode: "decimal" }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment
                              position='end'
                              sx={{
                                pl: 1.5,
                                ml: 1.5,
                                borderLeft: 1,
                                borderColor: "divider",
                              }}
                            >
                              %
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            bgcolor: "background.paper",
                          },
                          "& .MuiInputBase-input": {
                            textAlign: "right",
                            fontWeight: 600,
                            fontVariantNumeric: "tabular-nums",
                          },
                        }}
                        disabled={!canEditSchedule}
                      />
                    )}
                  </DeductionField>
                </Box>
              </Box>

              <Divider />
              <Box sx={{ p: { xs: 2, sm: 3 } }}>
                <Box role='group' aria-label='Expense cap' sx={settingsRowSx}>
                  <Box sx={{ flex: "1 1 240px", minWidth: 0 }}>
                    <Typography component='h3' variant='body1' fontWeight={600}>
                      Apply an expense cap?
                    </Typography>
                    <Typography
                      color='text.secondary'
                      sx={{ mt: 0.5, lineHeight: 1.6 }}
                    >
                      Limit the expenses eligible for the deduction rate above.
                    </Typography>
                  </Box>
                  <YesNoSwitch
                    value={isExpenseCapped}
                    onChange={handleIsExpenseCapped}
                    disabled={!canEditSchedule}
                    sx={switchSx}
                  />
                </Box>
                {isExpenseCapped && (
                  <Box sx={settingsGridSx}>
                    <DeductionField label='Expense cap (SGD)'>
                      {(inputProps) => (
                        <TaxField
                          {...deductionNumberProps}
                          value={expenseCappedAmount ?? ""}
                          onChange={handleExpenseCappedAmount}
                          inputProps={{ ...inputProps, inputMode: "decimal" }}
                          disabled={!canEditSchedule}
                        />
                      )}
                    </DeductionField>
                    {isForeignCurrency && (
                      <DeductionField
                        label={`Exchange rate (1 ${currency} to SGD)`}
                      >
                        {(inputProps) => (
                          <TaxField
                            {...deductionNumberProps}
                            value={exchangeRate}
                            onChange={handleExchangeRate}
                            inputProps={{ ...inputProps, inputMode: "decimal" }}
                            disabled={!canEditSchedule}
                            error={!isExchangeRateValid}
                            helperText={
                              isExchangeRateValid
                                ? `1 ${currency} = ${exchangeRate || "—"} SGD`
                                : "Enter an exchange rate greater than 0."
                            }
                          />
                        )}
                      </DeductionField>
                    )}
                  </Box>
                )}
              </Box>

              <Divider />
              <Box sx={{ p: { xs: 2, sm: 3 } }}>
                <Box
                  role='group'
                  aria-label='Excess deduction'
                  sx={settingsRowSx}
                >
                  <Box sx={{ flex: "1 1 240px", minWidth: 0 }}>
                    <Typography component='h3' variant='body1' fontWeight={600}>
                      Apply a deduction to expenses above the cap?
                    </Typography>
                    <Typography
                      color='text.secondary'
                      sx={{ mt: 0.5, lineHeight: 1.6 }}
                    >
                      {isExpenseCapped
                        ? "Apply a separate deduction rate to qualifying expenses exceeding the cap."
                        : "An expense cap is required to apply a deduction to expenses above the cap."}
                    </Typography>
                  </Box>
                  <YesNoSwitch
                    value={isExcessDeductionEnabled}
                    onChange={setIsExcessDeductionEnabled}
                    disabled={!canEditSchedule || !isExpenseCapped}
                    sx={switchSx}
                  />
                </Box>
                {isExpenseCapped && isExcessDeductionEnabled && (
                  <Box sx={{ mt: 3, display: "grid", gap: 3 }}>
                    <Box sx={{ maxWidth: 320 }}>
                      <DeductionField label='Deduction rate on excess expenses'>
                        {(inputProps) => (
                          <TaxField
                            {...deductionNumberProps}
                            value={excessDeductionRate}
                            onChange={(value) => {
                              if (
                                value === "" ||
                                (Number.isFinite(Number(value)) &&
                                  Number(value) >= 0)
                              ) {
                                setExcessDeductionRate(value);
                              }
                            }}
                            inputProps={{ ...inputProps, inputMode: "decimal" }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position='end'>
                                  %
                                </InputAdornment>
                              ),
                            }}
                            disabled={!canEditSchedule}
                          />
                        )}
                      </DeductionField>
                    </Box>
                    <DeductionField label='Excess deduction description (optional)'>
                      {(inputProps) => (
                        <TextFieldForName
                          value={excessDeductionDescription}
                          onChange={(value) =>
                            setExcessDeductionDescription(value)
                          }
                          inputProps={inputProps}
                          placeholder=''
                          disabled={!canEditSchedule}
                        />
                      )}
                    </DeductionField>
                  </Box>
                )}
              </Box>

              <Box
                sx={{
                  p: { xs: 2, sm: 3 },
                  borderTop: 1,
                  borderColor: "divider",
                  borderRadius: "0 0 12px 12px",
                  bgcolor: "action.hover",
                  display: "grid",
                  gap: 2,
                }}
              >
                <Typography component='h3' variant='body1' fontWeight={600}>
                  Deduction summary ({currency})
                </Typography>
                <Box
                  role='group'
                  aria-label={
                    isExpenseCapped
                      ? "Deduction within cap"
                      : "Further deduction"
                  }
                  sx={settingsRowSx}
                >
                  <Typography>
                    {isExpenseCapped
                      ? "Deduction within cap"
                      : "Further deduction"}
                  </Typography>
                  <Typography
                    sx={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}
                  >
                    {formatAmount(finalDeduction)}
                  </Typography>
                </Box>
                {isExpenseCapped && isExcessDeductionEnabled && (
                  <Box
                    role='group'
                    aria-label='Deduction above cap'
                    sx={settingsRowSx}
                  >
                    <Typography>Deduction above cap</Typography>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {formatAmount(excessDeduction)}
                    </Typography>
                  </Box>
                )}
                <Divider />
                <Box
                  role='group'
                  aria-label='Total further deduction'
                  sx={settingsRowSx}
                >
                  <Typography variant='h6' sx={{ fontWeight: 600 }}>
                    Total further deduction
                  </Typography>
                  <Typography
                    variant='h6'
                    sx={{
                      fontWeight: 700,
                      color: "primary.main",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {formatAmount(totalDeduction)}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </>
        )}
      </ScheduleContainer>

      <Divider />
      <ScheduleNotes
        notes={notes}
        setNotes={setNotes}
        reviewNotes={reviewNotes}
        setReviewNotes={setReviewNotes}
        enableEdit={canEditSchedule}
      />
    </Box>
  );
};

export default FurtherDeductionSchedule;
